import type { AtlasPresentationCommand, AtlasPresentationSnapshot } from './atlas-presentation';
import {
  type BrainTeamPresentationPlan,
  type BrainTeamPresentationReady,
  parseBrainTeamPresentationPlan,
} from './brain-team-presentation-plan';

/** Presence includes invalid/null payloads: a failed new contract cannot revive legacy effects. */
export function presentationDirective(value: unknown): {
  present: boolean;
  plan: BrainTeamPresentationPlan | null;
} {
  if (!value || typeof value !== 'object') return { present: false, plan: null };
  const raw = value as Record<string, unknown>;
  const snake = Object.prototype.hasOwnProperty.call(raw, 'presentation_plan');
  const camel = Object.prototype.hasOwnProperty.call(raw, 'presentationPlan');
  return {
    present: snake || camel || raw.presentationPlanDirectivePresent === true,
    plan: parseBrainTeamPresentationPlan(snake ? raw.presentation_plan : raw.presentationPlan),
  };
}

export type PresentationRequest = {
  requestId: string;
  scopeKey: string;
  snapshot: AtlasPresentationSnapshot;
};
export type PresentationCatalogTarget = {
  id: string;
  layer_id?: string;
  media_refs?: Array<{ kind: string; ref_id: string; label?: string }>;
};
const sameStory = (
  a: AtlasPresentationSnapshot['approved_story'],
  b: AtlasPresentationSnapshot['approved_story'],
) => a?.story_id === b?.story_id && a?.revision === b?.revision;

/** Pure host gate. The renderer repeats these checks against its live state.
 * Deliberate history replay refreshes revisions, never ownership or evidence. */
export function presentationCommand(input: {
  plan: BrainTeamPresentationPlan | null | undefined;
  current: AtlasPresentationSnapshot | null;
  request?: PresentationRequest | null;
  scopeKey: string;
  turnId?: string;
  explicit?: boolean;
  mapTargets: readonly PresentationCatalogTarget[];
  storyTargets: readonly PresentationCatalogTarget[];
  experienceLayers?: readonly { id: string; target_ids: string[] }[];
  experienceScenes?: readonly { id: string; target_id: string }[];
}): AtlasPresentationCommand | null {
  const { plan, current, request, explicit } = input;
  if (!plan || plan.status !== 'ready' || !current) return null;
  if (
    !sameStory(plan.approved_story, current.approved_story) ||
    plan.dataset_version !== current.dataset_version
  )
    return null;
  if (!explicit) {
    if (
      !request ||
      request.scopeKey !== input.scopeKey ||
      plan.request_id !== request.requestId ||
      plan.turn_id !== input.turnId ||
      request.snapshot.owner_key !== current.owner_key ||
      request.snapshot.view_revision !== current.view_revision ||
      request.snapshot.interaction_revision !== current.interaction_revision ||
      request.snapshot.dataset_version !== current.dataset_version ||
      !sameStory(request.snapshot.approved_story, current.approved_story) ||
      plan.base_view_revision !== request.snapshot.view_revision ||
      plan.base_interaction_revision !== request.snapshot.interaction_revision
    )
      return null;
  }
  const targets = new Map([...input.storyTargets, ...input.mapTargets].map((entry) => [entry.id, entry]));
  const drawable = new Set(input.mapTargets.map((entry) => entry.id));
  const action = plan.action;
  if (action.target_ids.some((id) => !targets.has(id) || (action.kind !== 'explain' && !drawable.has(id))))
    return null;
  const registerLayers = new Set(input.mapTargets.map((entry) => entry.layer_id).filter(Boolean));
  const experienceLayers = new Map(
    (input.experienceLayers ?? []).map((layer) => [layer.id, layer.target_ids]),
  );
  if (
    action.visible_layers.some((layer) =>
      layer.kind === 'register' ? !registerLayers.has(layer.id) : !experienceLayers.has(layer.id),
    )
  )
    return null;
  if (
    action.visible_layers.length &&
    action.target_ids.some(
      (id) =>
        !action.visible_layers.some((layer) =>
          layer.kind === 'register'
            ? targets.get(id)?.layer_id === layer.id
            : experienceLayers.get(layer.id)?.includes(id),
        ),
    )
  )
    return null;
  if (action.kind === 'resume' && action.resume_checkpoint_id !== current.resume_checkpoint_id) return null;
  if (action.scene_id && action.scene_id !== current.scene_id) return null;
  if (action.kind === 'explain') {
    const sceneAnchor = input.experienceScenes?.find((scene) => scene.id === current.scene_id)?.target_id;
    const selected = current.comparison_target_ids?.length
      ? current.comparison_target_ids
      : current.selected_target_ids.length
      ? current.selected_target_ids
      : sceneAnchor
      ? [sceneAnchor]
      : [];
    if (selected.length && action.target_ids.some((id) => !selected.includes(id))) return null;
    if (
      !selected.length &&
      action.target_ids.some(
        (id) =>
          !current.visible_layers.some((layer) =>
            layer.kind === 'register'
              ? targets.get(id)?.layer_id === layer.id
              : experienceLayers.get(layer.id)?.includes(id),
          ),
      )
    )
      return null;
  }
  return {
    plan_id: plan.plan_id,
    owner_key: current.owner_key,
    base_view_revision: current.view_revision,
    base_interaction_revision: current.interaction_revision,
    ...(current.approved_story ? { approved_story: current.approved_story } : {}),
    ...(current.dataset_version ? { dataset_version: current.dataset_version } : {}),
    action,
    ...(explicit ? { explicit: true } : {}),
  };
}

/** A deliberate card action uses the same guarded command path as the whole answer. */
export function presentationForTarget(
  plan: BrainTeamPresentationReady,
  targetId: string,
): BrainTeamPresentationReady | null {
  const card = plan.evidence_cards.find((entry) => entry.target_id === targetId);
  if (!card) return null;
  return {
    ...plan,
    plan_id: `${plan.plan_id}:target:${plan.action.target_ids.indexOf(targetId)}`,
    action: {
      kind: 'show',
      target_ids: [targetId],
      visible_layers: [],
      camera: 'fit_targets',
      selection: 'replace',
    },
    evidence_cards: [card],
    media_refs: plan.media_refs.filter((ref) => ref.target_id === targetId),
    text_sections: [],
  };
}
